import React, { useState, useEffect } from 'react';
import { Card, Button, List, message, Modal, Table, Select, Upload, Popconfirm, Input } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchReportsByVehicle,
  createDamageReport,
  fetchDamageReport,
  updateDamageReport,
  uploadDamageReportPhoto,
  deleteDamageReportPhoto,
  deleteDamageReport,
  DAMAGE_TYPE_OPTIONS,
  type DamageAssessmentReportListItemDto,
  type DamageAssessmentReportDto,
  type DamageAssessmentReportItemDto,
} from '../services/damageAssessmentService';
import type { UploadFile } from 'antd/es/upload/interface';
import { useCanDelete } from '../hooks/useCanDelete';

const { TextArea } = Input;

const ZONE_NAMES: Record<number, string> = {
  1: 'Ön Sağ Çamurluk',
  2: 'Ön Sağ Kapı',
  3: 'Arka Sağ Kapı',
  4: 'Arka Sağ Çamurluk',
  5: 'Arka Tampon',
  6: 'Arka Kaput',
  7: 'Arka Sol Çamurluk',
  8: 'Arka Sol Kapı',
  9: 'Ön Sol Kapı',
  10: 'Ön Sol Çamurluk',
  11: 'Ön Tampon',
  12: 'Kaput',
  13: 'Tavan',
};

const ZONE_HOTSPOTS: { zone: number; left: number; top: number; width: number; height: number }[] = [
  { zone: 1, left: 65, top: 15, width: 14, height: 16 },
  { zone: 2, left: 48, top: 5, width: 10, height: 16 },
  { zone: 3, left: 35, top: 5, width: 10, height: 16 },
  { zone: 4, left: 10, top: 5, width: 10, height: 16 },
  { zone: 5, left: 0, top: 40, width: 8, height: 22 },
  { zone: 6, left: 15, top: 40, width: 8, height: 22 },
  { zone: 7, left: 10, top: 75, width: 10, height: 12 },
  { zone: 8, left: 35, top: 75, width: 10, height: 12 },
  { zone: 9, left: 48, top: 75, width: 10, height: 12 },
  { zone: 10, left: 69, top: 75, width: 10, height: 16 },
  { zone: 11, left: 90, top: 40, width: 8, height: 22 },
  { zone: 12, left: 70, top: 40, width: 8, height: 22 },
  { zone: 13, left: 40, top: 40, width: 15, height: 22 },
];

interface DamageAssessmentSectionProps {
  vehicleId: number | null;
  customerId: number | null;
  serviceOrderId?: number | null;
  vehiclePlate?: string;
  customerName?: string;
  disabled?: boolean;
}

const DamageAssessmentSection: React.FC<DamageAssessmentSectionProps> = ({
  vehicleId,
  customerId,
  serviceOrderId,
  vehiclePlate = '',
  customerName = '',
  disabled,
}) => {
  const canDelete = useCanDelete();
  const [list, setList] = useState<DamageAssessmentReportListItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [report, setReport] = useState<DamageAssessmentReportDto | null>(null);
  const [reportLoading, setReportLoading] = useState(false); // used when opening existing report
  const [items, setItems] = useState<DamageAssessmentReportItemDto[]>([]);
  const [notesLocal, setNotesLocal] = useState('');
  const [saving, setSaving] = useState(false);
  const [photoList, setPhotoList] = useState<UploadFile[]>([]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const isCreateMode = !report;

  const loadList = async () => {
    if (!vehicleId) return;
    setLoading(true);
    try {
      const data = await fetchReportsByVehicle(vehicleId);
      setList(data);
    } catch {
      message.error('Tutanak listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [vehicleId]);

  const openCreateModal = () => {
    if (!vehicleId || !customerId) {
      message.warning('Önce müşteri ve araç seçin.');
      return;
    }
    setReport(null);
    setItems([]);
    setNotesLocal('');
    setPhotoList([]);
    setModalOpen(true);
  };

  const handleOpenReport = async (id: number) => {
    setModalOpen(true);
    setReport(null);
    setReportLoading(true);
    try {
      const data = await fetchDamageReport(id);
      setReport(data);
      setItems(data.items.map((i) => ({ ...i, zoneName: ZONE_NAMES[i.zoneNumber] ?? i.zoneName })));
      setNotesLocal(data.notes ?? '');
      setPhotoList(data.photos.map((p) => ({ uid: String(p.id), name: p.fileName, url: p.url, status: 'done' as const })));
    } catch {
      message.error('Tutanak yüklenemedi');
      setModalOpen(false);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (isCreateMode) {
      if (!vehicleId || !customerId) return;
      setSaving(true);
      try {
        const created = await createDamageReport({
          vehicleId,
          customerId,
          serviceOrderId,
          notes: notesLocal || undefined,
        });
        await updateDamageReport(created.id, {
          notes: notesLocal || undefined,
          items: items.map((i) => ({ zoneNumber: i.zoneNumber, damageType: i.damageType })),
        });
        for (const file of photoList) {
          const raw = file.originFileObj ?? (file as UploadFile & { originFileObj?: File }).originFileObj;
          if (raw instanceof File) {
            await uploadDamageReportPhoto(created.id, raw);
          }
        }
        message.success('Tutanak oluşturuldu');
        setModalOpen(false);
        setReport(null);
        setItems([]);
        setNotesLocal('');
        setPhotoList([]);
        loadList();
      } catch {
        message.error('Tutanak oluşturulamadı');
      } finally {
        setSaving(false);
      }
    } else {
      if (!report) return;
      setSaving(true);
      try {
        const updated = await updateDamageReport(report.id, {
          notes: notesLocal ?? report.notes ?? undefined,
          items: items.map((i) => ({ zoneNumber: i.zoneNumber, damageType: i.damageType })),
        });
        setReport(updated);
        setItems(updated.items.map((i) => ({ ...i, zoneName: ZONE_NAMES[i.zoneNumber] })));
        message.success('Tutanak kaydedildi');
        loadList();
      } catch {
        message.error('Kaydetme başarısız');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleZoneClick = (zoneNumber: number) => {
    const zoneName = ZONE_NAMES[zoneNumber];
    setItems((prev) => [...prev, { zoneNumber, zoneName, damageType: DAMAGE_TYPE_OPTIONS[0] }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDamageTypeChange = (index: number, damageType: string) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, damageType } : it)));
  };

  const handlePhotoUpload = async (file: File) => {
    if (isCreateMode) {
      setPhotoList((prev) => [...prev, { uid: String(Date.now()) + file.name, name: file.name, status: 'done' as const, originFileObj: file } as UploadFile]);
      message.success('Fotoğraf eklendi (kaydedince yüklenecek)');
    } else if (report) {
      try {
        const photo = await uploadDamageReportPhoto(report.id, file);
        setPhotoList((prev) => [...prev, { uid: String(photo.id), name: photo.fileName, url: photo.url, status: 'done' as const }]);
        setReport((r) => (r ? { ...r, photos: [...r.photos, photo] } : null));
        message.success('Fotoğraf yüklendi');
      } catch {
        message.error('Yükleme başarısız');
      }
    }
    return false;
  };

  const handlePhotoRemove = async (uid: string) => {
    if (isCreateMode) {
      setPhotoList((prev) => prev.filter((f) => f.uid !== uid));
      return;
    }
    if (!report) return;
    const photoId = parseInt(uid, 10);
    if (Number.isNaN(photoId)) {
      setPhotoList((prev) => prev.filter((f) => f.uid !== uid));
      return;
    }
    try {
      await deleteDamageReportPhoto(report.id, photoId);
      setPhotoList((prev) => prev.filter((f) => f.uid !== uid));
      setReport((r) => (r ? { ...r, photos: r.photos.filter((p) => p.id !== photoId) } : null));
    } catch {
      message.error('Silinemedi');
    }
  };

  const handleDeleteReport = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteDamageReport(id);
      message.success('Tutanak silindi');
      loadList();
    } catch {
      message.error('Silinemedi');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePrint = async (item: DamageAssessmentReportListItemDto) => {
    try {
      const data = await fetchDamageReport(item.id);
      const dateStr = dayjs(data.createdAt).format('DD.MM.YYYY');
      const timeStr = dayjs(data.createdAt).format('HH:mm');
      const rows = (data.items || []).map(
        (i) =>
          `<tr>
            <td style="text-align:center;padding:8px 6px;border-bottom:1px solid #e8e8e8;">${i.zoneNumber}</td>
            <td style="padding:8px 6px;border-bottom:1px solid #e8e8e8;">${i.zoneName || ZONE_NAMES[i.zoneNumber] || i.zoneNumber}</td>
            <td style="padding:8px 6px;border-bottom:1px solid #e8e8e8;">${i.damageType || '—'}</td>
          </tr>`
      ).join('');
      const notesHtml = data.notes ? `<div class="notes-section" style="margin: 16px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;"><h4 style="margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50;">Notlar</h4><div style="white-space: pre-wrap;">${String(data.notes).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div></div>` : '';
      const selectedZoneNumbers = Array.from(new Set((data.items || []).map((i) => i.zoneNumber)));
      const carOverlayHtml = selectedZoneNumbers
        .map((zoneNum) => {
          const h = ZONE_HOTSPOTS.find((z) => z.zone === zoneNum);
          if (!h) return '';
          return `<div class="zone-x" style="left:${h.left}%;top:${h.top}%;width:${h.width}%;height:${h.height}%;"><span>✕</span></div>`;
        })
        .join('');
      const printContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Hasar Tespit Tutanağı</title>
  <style>
    @page { size: A4; margin: 15mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; margin: 0; padding: 15mm; color: #1a1a1a; font-size: 12px; }
    .logo-area { text-align: center; margin-bottom: 16px; }
    .logo-area img { width: 100%; max-width: 100%; height: auto; max-height: 40mm; object-fit: contain; display: block; }
    .title { text-align: center; font-size: 20px; font-weight: bold; margin: 16px 0 20px 0; color: #1a1a1a; }
    .order-info { margin-bottom: 16px; font-size: 13px; }
    .info-row { display: flex; gap: 20px; margin-bottom: 16px; }
    .info-col { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9; }
    .info-col h4 { margin: 0 0 8px 0; font-size: 13px; font-weight: 600; color: #2c3e50; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
    .car-diagram-wrap { position: relative; width: 100%; max-width: 500px; margin: 12px auto 16px; }
    .car-diagram-wrap img { width: 100%; height: auto; display: block; }
    .car-diagram-wrap .zone-x { position: absolute; display: flex; align-items: center; justify-content: center; pointer-events: none; }
    .car-diagram-wrap .zone-x span { color: #c00; font-size: 22px; font-weight: bold; text-shadow: 0 0 1px #fff, 0 0 2px #fff; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0 20px 0; }
    th { text-align: left; padding: 10px 6px; background: #2c3e50; color: #fff; font-weight: 600; font-size: 11px; }
    td { font-size: 12px; padding: 8px 6px; border-bottom: 1px solid #e8e8e8; }
  </style>
</head>
<body>
  <div class="logo-area">
    <img src="${window.location.origin}/images/logo-print-full.png" alt="Logo" onerror="this.style.display='none'">
  </div>
  <div class="title">Hasar Tespit Tutanağı</div>
  <div class="order-info"><strong>Tutanak No:</strong> ${data.reportNo} &nbsp; <strong>Tarih:</strong> ${dateStr} &nbsp; <strong>Saat:</strong> ${timeStr}</div>
  <div class="info-row">
    <div class="info-col"><h4>Müşteri</h4><div>${(data.customerName || '').replace(/</g, '&lt;')}</div></div>
    <div class="info-col"><h4>Araç</h4><div>Plaka: ${(data.vehiclePlate || '').replace(/</g, '&lt;')}</div></div>
  </div>
  <div class="car-diagram-wrap">
    <img src="${window.location.origin}/images/car-damage-zones.png" alt="Araç bölgeleri" style="width:100%;height:auto;">
    ${carOverlayHtml}
  </div>
  <table>
    <thead><tr><th style="width:40px;">Bölge No</th><th>Bölge</th><th>Hasar tipi</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="3" style="text-align:center;color:#999;">Kayıt yok</td></tr>'}</tbody>
  </table>
  ${notesHtml}
</body>
</html>`;
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(printContent);
        // Escaping </script> so it doesn't close the script tag in the HTML source
        win.document.write('<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<' + '/script>');
        win.document.close();
      }
    } catch {
      message.error('Yazdırma açılamadı');
    }
  };

  const canAdd = !!vehicleId && !!customerId && !disabled;
  const displayPlate = report ? report.vehiclePlate : vehiclePlate;
  const displayCustomer = report ? report.customerName : customerName;

  return (
    <Card title="Hasar Tespit Tutanağı" style={{ marginTop: 16 }}>
      {!vehicleId ? (
        <p style={{ color: '#666' }}>Tutanak listesi için önce araç seçin.</p>
      ) : (
        <>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: '#666' }}>Bu plakaya ait tutanaklar:</span>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal} disabled={!canAdd}>
              Ekle
            </Button>
          </div>
          <List
            loading={loading}
            dataSource={list}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                actions={[
                  <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleOpenReport(item.id)} title="Aç" />,
                  <Button type="text" size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(item)} title="Yazdır" />,
                  ...(canDelete
                    ? [<Popconfirm
                        key="del"
                        title="Bu tutanak silinecek (tüm satırlar ve ekli dosyalar). Emin misiniz?"
                        onConfirm={() => handleDeleteReport(item.id)}
                        okText="Sil"
                        cancelText="İptal"
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="text" danger size="small" icon={<DeleteOutlined />} loading={deletingId === item.id} title="Sil" />
                      </Popconfirm>]
                    : []),
                ]}
              >
                <List.Item.Meta title={`${dayjs(item.createdAt).format('DD.MM.YYYY - HH:mm')} — ${item.reportNo}`} />
              </List.Item>
            )}
            locale={{ emptyText: 'Henüz tutanak yok. "Ekle" ile yeni tutanak oluşturabilirsiniz.' }}
          />
        </>
      )}

      <Modal
        title={isCreateMode ? 'Yeni Hasar Tespit Tutanağı' : `Hasar Tespit Tutanağı — ${report?.reportNo}`}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          if (isCreateMode) {
            setReport(null);
            setItems([]);
            setNotesLocal('');
            setPhotoList([]);
          }
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setModalOpen(false)}>
            {isCreateMode ? 'İptal' : 'Kapat'}
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSaveReport}>
            {isCreateMode ? 'Kaydet' : 'Kaydet'}
          </Button>,
        ]}
      >
        {reportLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Yükleniyor...</div>
        ) : (
          <>
        <p style={{ marginBottom: 12 }}>
          <strong>Plaka:</strong> {displayPlate || '—'} &nbsp; <strong>Müşteri:</strong> {displayCustomer || '—'}
        </p>
        <p style={{ marginBottom: 16, color: '#666', fontSize: 12 }}>
          Aşağıdaki araç görselinde numaralı bölgelere tıklayarak hasar satırı ekleyin. Her satır için hasar tipini seçin.
        </p>
        <div style={{ position: 'relative', width: '100%', maxWidth: 600, margin: '0 auto 16px' }}>
          <img src="/images/car-damage-zones.png" alt="Araç bölgeleri" style={{ width: '100%', height: 'auto', display: 'block' }} draggable={false} />
          <div style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}>
            {ZONE_HOTSPOTS.map(({ zone, left, top, width, height }) => (
              <div
                key={zone}
                role="button"
                tabIndex={0}
                onClick={() => handleZoneClick(zone)}
                onKeyDown={(e) => e.key === 'Enter' && handleZoneClick(zone)}
                style={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, cursor: 'pointer' }}
                title={`Bölge ${zone}: ${ZONE_NAMES[zone]}`}
              />
            ))}
          </div>
        </div>
        <Table
          size="small"
          dataSource={items}
          rowKey={(_, i) => String(i)}
          pagination={false}
          columns={[
            { title: 'Bölge', dataIndex: 'zoneNumber', width: 80, render: (z: number) => `${z} - ${ZONE_NAMES[z] ?? ''}` },
            {
              title: 'Hasar tipi',
              dataIndex: 'damageType',
              render: (val: string, _: DamageAssessmentReportItemDto, index: number) => (
                <Select
                  value={val}
                  onChange={(v) => handleDamageTypeChange(index, v)}
                  options={DAMAGE_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))}
                  style={{ width: '100%', minWidth: 140 }}
                />
              ),
            },
            {
              title: '',
              width: 50,
              render: (_, __, index) =>
                canDelete ? (
                  <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveItem(index)} />
                ) : null,
            },
          ]}
          locale={{ emptyText: 'Bölgelere tıklayarak satır ekleyin' }}
        />
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Notlar (araçla ilgili)</div>
          <TextArea
            rows={3}
            value={notesLocal}
            onChange={(e) => setNotesLocal(e.target.value)}
            placeholder="Araçla ilgili notlarınızı yazabilirsiniz..."
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Araç fotoğrafları (isteğe bağlı)</div>
          <Upload
            listType="picture-card"
            fileList={photoList}
            beforeUpload={handlePhotoUpload}
            onRemove={({ uid }) => handlePhotoRemove(uid)}
            accept="image/*"
            multiple
          >
            {photoList.length >= 10 ? null : (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Yükle</div>
              </div>
            )}
          </Upload>
        </div>
          </>
        )}
      </Modal>
    </Card>
  );
};

export default DamageAssessmentSection;
